import { ColorAlphaScalePaletteDefinition, PALETTES, SnsPaletteDefinition } from './palettes';
import { PaletteSettings } from '@app/api/mapping/types';

export function colorHexToNumArray(hexColor: string): [number, number, number, number] {
  // Remove the # if present
  const hex = hexColor.replace('#', '');

  // Parse the hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Default alpha to 255 (fully opaque) if not provided
  const a = hex.length === 8 ? parseInt(hex.substring(6, 8), 16) : 255;

  return [r, g, b, a];
}

export type RenderedRGBPalette = [number, [number, number, number]][];
export type RenderedRGBAPalette = [number, [number, number, number, number]][];
export type RenderedPalette = RenderedRGBPalette | RenderedRGBAPalette;

export function renderPalette(
  min: number,
  max: number,
  palette: PaletteSettings,
  multiply?: number,
): RenderedPalette {
  if (multiply == null) {
    multiply = 1;
  }
  // Handle the new palette structure
  let colors: number[][];
  if (palette.kind === 'sns') {
    const paletteDefinition = PALETTES[palette.id] as SnsPaletteDefinition;
    colors = paletteDefinition.colors;
  } else if (palette.kind === 'color_alpha_scale') {
    // Generate colors based on the user-selected color and alphas
    const paletteDefinition = PALETTES[palette.id] as ColorAlphaScalePaletteDefinition;
    const baseColor = colorHexToNumArray(palette.color || '#000000');
    const alphas = paletteDefinition.alphas;
    colors = alphas.map((alpha) => [baseColor[0], baseColor[1], baseColor[2], alpha]);
  } else if (palette.kind === 'union') {
    const nPalettes = palette.palettes.length;
    const stepSize = (max - min) / nPalettes;
    let renderedPalettes = [];
    for (let i = 0; i < nPalettes; i++) {
      const stepMin = min + i * stepSize;
      const stepMax = stepMin + stepSize;
      renderedPalettes = renderedPalettes.concat(
        renderPalette(stepMin, stepMax, palette.palettes[i], multiply),
      );
    }
    return renderedPalettes;
  } else {
    console.warn('Unexpected palette settings:', palette);
    return [];
  }

  const n_colors = colors.length;
  const n_channels = colors[0].length;

  // Create evenly spaced values between min and max
  const values: number[] = [];
  for (let i = 0; i < n_colors; i++) {
    const value = min + (max - min) * (i / (n_colors - 1));
    values.push(value * multiply);
  }

  if (n_channels == 3) {
    const rendered: [number, [number, number, number]][] = [];
    for (let i = 0; i < values.length; i++) {
      const color = colors[i];
      rendered.push([values[i], [color[0], color[1], color[2]]]);
    }
    return rendered;
  }

  if (n_channels == 4) {
    const rendered: [number, [number, number, number, number]][] = [];
    for (let i = 0; i < values.length; i++) {
      const color = colors[i];
      rendered.push([values[i], [color[0], color[1], color[2], color[3]]]);
    }
    return rendered;
  }

  return [];
}
